import type React from "react";
import {useCallback, useEffect, useState} from "react";
import {useAuthState} from "react-firebase-hooks/auth";
import {useParams} from "react-router-dom";

import {faPlus, faSpinner} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

import {useAddLevelToPlaylist} from "~/api/hooks/useAddLevelToPlaylist";
import {usePlaylists} from "~/api/hooks/usePlaylist";
import CustomButton from "~/component/common/CustomButton";
import {auth} from "~/firebase/firebase";

import useModal from "../../../../hooks/useModal";

const AddLevelToPlaylist: React.FC = () => {
  const {id: levelId} = useParams<{id: string}>();
  const [myUser] = useAuthState(auth);
  const {openModal, closeModal, Modal} = useModal();

  const {data: playlists} = usePlaylists({
    key: {},
    limit: 100,
    filters: [
      {
        fieldPath: "creatorId",
        opStr: "==",
        value: myUser?.uid ?? "unknown",
      },
    ],
    sort: [{fieldPath: "updatedAt", direction: "desc"}],
  });
  const [selectedPlaylist, setSelectedPlaylist] = useState<string>("");

  useEffect(() => {
    if (playlists && playlists.length > 0 && !selectedPlaylist) {
      // Find the first playlist that doesn't already contain the level and isn't immutable
      const validPlaylist = playlists.find(
        (playlist) =>
          !playlist.levelIds.includes(levelId!) && !playlist.isImmutable,
      );
      if (validPlaylist) {
        setSelectedPlaylist(validPlaylist.id);
      }
    }
  }, [playlists, selectedPlaylist, levelId]);

  const {
    mutate: addLevelToPlaylistMutation,
    isPending: isAddingLevelToPlaylist,
  } = useAddLevelToPlaylist();

  const handleSelectChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setSelectedPlaylist(e.target.value);
    },
    [],
  );

  const handleAddLevel = useCallback(() => {
    if (!selectedPlaylist || !levelId) return;
    addLevelToPlaylistMutation({
      data: {
        id: selectedPlaylist,
        levelId,
      },
    });
    closeModal();
  }, [selectedPlaylist, levelId, addLevelToPlaylistMutation, closeModal]);

  // Filter valid playlists (those that don't already contain the level and aren't immutable)
  const validPlaylists =
    playlists?.filter(
      (playlist) =>
        !playlist.levelIds.includes(levelId!) && !playlist.isImmutable,
    ) || [];

  return (
    <div className="flex items-center justify-center h-10">
      <CustomButton
        onClick={openModal}
        aria-label="Add Level to Playlist"
        disabled={validPlaylists.length === 0}
        className={`w-10 h-10 ${
          validPlaylists.length === 0
            ? "bg-gray-500 cursor-not-allowed"
            : "bg-green-500 hover:bg-green-600"
        } text-white p-0 rounded-full transition-colors flex items-center justify-center`}
      >
        {isAddingLevelToPlaylist ? (
          <FontAwesomeIcon icon={faSpinner} spin size="xl" />
        ) : (
          <FontAwesomeIcon icon={faPlus} size="xl" />
        )}
      </CustomButton>

      <Modal>
        <div>
          <h3 className="text-xl mb-4 text-white">Add to Playlist</h3>
          <div className="mb-4">
            <label
              htmlFor="playlist-select"
              className="block mb-2 text-sm font-medium text-white"
            >
              Select a playlist:
            </label>
            <select
              id="playlist-select"
              value={selectedPlaylist}
              onChange={handleSelectChange}
              className="w-full p-2 mb-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none"
            >
              {validPlaylists.map((playlist) => (
                <option key={playlist.id} value={playlist.id}>
                  {playlist.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex justify-end space-x-2">
            <CustomButton
              onClick={closeModal}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
            >
              Cancel
            </CustomButton>
            <CustomButton
              onClick={handleAddLevel}
              disabled={!selectedPlaylist}
              className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
            >
              Add
            </CustomButton>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AddLevelToPlaylist;
